import React, {Fragment, useCallback} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {constants} from '@neosvoc/nv-platform-core-common';
import Yup from '../../yup';
import messages from '../../../res/translations/messages';
import Alert from '../Alert';
import Icon from '../Icon';
import FormGroupField from './FormGroupField';
import FormGroupCheckbox from './FormGroupCheckbox';
import Error from '../Error';
import RolesField from './RolesField';
import PrivilegeBadges from './PrivilegeBadges';
import {get} from 'lodash';
// import {SEND_PROVIDERS} from '../../constants';
import SendProviders from './sendProviders/SendProviders';
import {sendProvidersSchema} from './sendProviders/sendProvidersSchema';
import {TRACKERS_ARCHIVE_OPTIONS} from '../../constants';
import SyncTrackersArchiveButton from './SyncTrackersArchiveButton';


const UserEditSchema = Yup.object().shape({
    email: Yup.string().required().email(),
    name: Yup.string().required(),
    enabled: Yup.boolean().required(),
    roles: Yup.array().required().of(Yup.string().required().oneOf(Object.values(constants.USER_ROLES))),
    password: Yup.string(),
    password2: Yup.string().when(['password'], {is: (password) => !!password, then: Yup.string().required().oneOf([Yup.ref('password')])}),
    organization: Yup.object().shape({
        allowEvents: Yup.boolean(),
        allowAnalytics: Yup.boolean(),
        name: Yup.string().required(),
        // pinpointAppId: Yup.string(),
        tenantId: Yup.string(),
        emailFrom: Yup.string().email(),
        emailFromName: Yup.string().max(32),
        smsFrom: Yup.string().max(11).matches(constants.GSM_7BIT_ALPHABET_REGEX, 'gsmAlphabet'),
        sendProviders: sendProvidersSchema,
        trackerArchiveFrequency: Yup.string().required().oneOf(Object.values(TRACKERS_ARCHIVE_OPTIONS)),
        trackerArchiveFrequencyValue: Yup.number().when('trackerArchiveFrequency', {
            is: (val) => val === 'trackerCreated',
            then: Yup.number().required().min(1),
            otherwise: Yup.number().required().min(0),
        }),
    }).required(),
});

const UserEditForm = ({error, item, loading, onSubmit, onSync}) => {
    const archiveOptions = Object.values(TRACKERS_ARCHIVE_OPTIONS).map((option) => ({value: option, label: option}));

    const handleSyncConfirm = useCallback((id, trackerArchiveFrequency, trackerArchiveFrequencyValue) => {
        if (onSync) onSync(id, trackerArchiveFrequency, trackerArchiveFrequencyValue);
    }, [onSync]);

    const renderForm = ({setFieldValue, values, errors, touched, isValid}) => {
        return (
            <Fragment>
                {error && <Alert error={error} />}
                <Form>
                    <div className="survey-create-box">
                        <div className="col">
                            <FormGroupField type="email" id="uef-email" name="email" label={messages.admin.users.email} readOnly />
                            <FormGroupCheckbox id="uef-enabled" name="enabled" label={messages.admin.users.enabled} />
                            <FormGroupField type="text" id="uef-name" name="name" label={messages.admin.users.name} required />
                            <div className="row">
                                <div className="col">
                                    <FormGroupField type="password" id="uef-password" name="password" label={messages.admin.users.password} />
                                </div>
                                <div className="col">
                                    <FormGroupField type="password" id="uef-password2" name="password2" label={messages.admin.users.password2} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="col-form-label"><FormattedMessage {...messages.admin.users.roles} /></label>
                                <Field name="roles" component={RolesField} id="uef-roles" />
                                <ErrorMessage name="roles">{(message) => <Error reason={message.key} reasonParams={message.params} />}</ErrorMessage>
                                <div className="mt-2">
                                    <span className="mr-2"><FormattedMessage {...messages.admin.users.effectivePrivileges} /></span>
                                    <PrivilegeBadges user={values} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="col-form-label"><FormattedMessage {...messages.admin.organization.title} /></label>
                                <FormGroupField type="text" id="uef-organization-name" name="organization.name"
                                    label={messages.admin.organization.name} />
                                <FormGroupCheckbox id="uef-organization-allowEvents" name="organization.allowEvents"
                                    label={messages.admin.organization.allowEvents} />
                            </div>
                            <div className="form-group">
                                <label className="col-form-label"><FormattedMessage {...messages.admin.organization.dashboard} /></label>
                                <FormGroupCheckbox name="organization.allowAnalytics" id="uef-organization-allowAnalytics"
                                    label={messages.admin.organization.allowAnalytics} />
                                {values.organization.allowAnalytics ? <FormGroupField type="text" id="uef-organization-tenantId"
                                    name="organization.tenantId"
                                    label={messages.admin.organization.tenantId} maxLength="32" /> : null}
                            </div>
                            <div className="col send-provider-params">
                                <label className="col-form-label">
                                    <FormattedMessage {...messages.admin.organization.label.sendProviders} />
                                </label>
                                <div className="form-group">
                                    <label className="col-form-label"><FormattedMessage {...messages.admin.organization.sendProviders.title} /></label>
                                    {/* <FormGroupField type="text" id="uef-organization-pinpointAppId" name="organization.pinpointAppId"
                                    label={messages.admin.organization.pinpointAppId} /> */}
                                    <div className="row">
                                        <div className="col">
                                            <SendProviders setFieldValue={setFieldValue} values={values.organization.sendProviders} />
                                            <ErrorMessage name="organization.sendProviders">
                                                {(message) => <Error reason="sendProvidersConfig" />}
                                            </ErrorMessage>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <FormGroupField type="email" id="uef-organization-emailFrom" name="organization.emailFrom"
                                                label={messages.admin.organization.emailFrom} />
                                        </div>
                                        <div className="col">
                                            <FormGroupField type="text" id="uef-organization-emailFromName" name="organization.emailFromName"
                                                label={messages.admin.organization.emailFromName} maxLength="32" />
                                        </div>
                                        <div className="col">
                                            <FormGroupField type="text" id="uef-organization-smsFrom" name="organization.smsFrom"
                                                label={messages.admin.organization.smsFrom} maxLength="11" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col send-provider-params">
                                <label className="col-form-label">
                                    <FormattedMessage {...messages.admin.organization.label.trackersArchive} />
                                </label>
                                <FormGroupField
                                    component="select"
                                    id="user_create_form-trackerArchiveFrequency"
                                    name="organization.trackerArchiveFrequency"
                                    label={messages.admin.organization.trackerArchiveFrequency}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onChange={(e) => {
                                        setFieldValue('organization.trackerArchiveFrequencyValue', e.target.value === 'trackerCreated' ? 1 : 0);
                                        setFieldValue('organization.trackerArchiveFrequency', e.target.value);
                                    }}
                                >
                                    {archiveOptions.map(({value, label}) => {
                                        return (<FormattedMessage key={value} {...messages.admin.organization.trackerArchiveOptions[label]}>
                                            {(txt) => <option value={value}>{txt}</option>}
                                        </FormattedMessage>);
                                    })}
                                </FormGroupField>
                                {values.organization && values.organization.trackerArchiveFrequency !== 'never' && (
                                    <FormGroupField
                                        type="number"
                                        min={values.organization.trackerArchiveFrequency === 'trackerCreated' ? 1 : 0}
                                        id="user_create_form-trackerArchiveFrequencyValue"
                                        name="organization.trackerArchiveFrequencyValue"
                                        label={messages.admin.organization.trackerArchiveFrequencyValue} />
                                )}

                                <div className="form-group">
                                    <SyncTrackersArchiveButton
                                        itemId={item && item.id}
                                        trackerArchiveFrequency={values.organization.trackerArchiveFrequency}
                                        trackerArchiveFrequencyValue={values.organization.trackerArchiveFrequencyValue}
                                        onConfirm={handleSyncConfirm}
                                    />
                                </div>

                                <div className="form-group mb-10">
                                    <FormGroupCheckbox id="uef-organization-allowTrackerArchiveManagement" name="organization.allowTrackerArchiveManagement"
                                        label={messages.admin.organization.allowTrackerArchiveManagement} />
                                </div>
                            </div>
                        </div>

                        <div className="col text-right mt-10">
                            <Link className="btn btn-outline-secondary mr-3" to="/admin/users">
                                <FormattedMessage {...messages.buttons.cancel} />
                            </Link>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                <Icon icon="new-doc" /> <FormattedMessage {...messages.buttons.save} />
                            </button>
                        </div>
                    </div>
                </Form>
            </Fragment>
        );
    };

    return (
        <Formik
            initialValues={{
                email: item.email,
                name: item.name,
                enabled: item.enabled,
                roles: item.roles,
                password: '',
                password2: '',
                organization: {
                    allowEvents: !!(item.organization && item.organization.allowEvents),
                    allowAnalytics: !!(item.organization && item.organization.allowAnalytics),
                    name: item.organization && item.organization.name || '',
                    // pinpointAppId: item.organization && item.organization.pinpointAppId || '',
                    tenantId: item.organization && item.organization.tenantId || '',
                    emailFrom: item.organization && item.organization.emailFrom || '',
                    emailFromName: item.organization && item.organization.emailFromName || '',
                    smsFrom: item.organization && item.organization.smsFrom || '',
                    trackerArchiveFrequency: item.organization.trackerArchiveFrequency || 'never',
                    trackerArchiveFrequencyValue: item.organization.trackerArchiveFrequencyValue || 0,
                    sendProviders: {
                        pinpoint: {
                            enabled: !!get(item, 'organization.sendProviders.pinpoint.enabled'),
                            parameters: {
                                applicationId: get(item, 'organization.sendProviders.pinpoint.parameters.applicationId') || '',
                            },
                        },
                        mapp: {
                            enabled: !!get(item, 'organization.sendProviders.mapp.enabled'),
                            parameters: {
                                baseURL: get(item, 'organization.sendProviders.mapp.parameters.baseURL') || '',
                                groupId: get(item, 'organization.sendProviders.mapp.parameters.groupId') || '',
                                smsChannelId: get(item, 'organization.sendProviders.mapp.parameters.smsChannelId') || '',
                                user: get(item, 'organization.sendProviders.mapp.parameters.user') || '',
                                password: get(item, 'organization.sendProviders.mapp.parameters.password') || '',
                            },
                        },
                        aws: {
                            enabled: !!get(item, 'organization.sendProviders.aws.enabled'),
                            parameters: {
                                email: {
                                    configurationSet: get(item, 'organization.sendProviders.aws.parameters.email.configurationSet') || '',
                                },
                                sms: {
                                    configurationSetName: get(item, 'organization.sendProviders.aws.parameters.sms.configurationSetName') || '',
                                    optOutListName: get(item, 'organization.sendProviders.aws.parameters.sms.optOutListName') || '',
                                },
                                whatsapp: {
                                    wabaId: get(item, 'organization.sendProviders.aws.parameters.whatsapp.wabaId') || '',
                                    originationPhoneNumberId: get(item, 'organization.sendProviders.aws.parameters.whatsapp.originationPhoneNumberId') || '',
                                },
                            },
                        },
                    },
                    allowTrackerArchiveManagement: !!get(item, 'organization.allowTrackerArchiveManagement'),
                },
            }}
            validationSchema={UserEditSchema}
            onSubmit={onSubmit}
        >
            {renderForm}
        </Formik>
    );
};

export default UserEditForm;
